import { useRef } from 'react'
import { useAppSelector } from '@services/store'
import {
	REQUEST_CONFIGS_KEYS,
	REQUEST_TN_DETAILS_STEPS_NAME
} from '@services/constants'
import requestForm from '../../request.form'
import { isGR7_3HomeOfficeOrBusiness } from '../__index.utils'

export const STEP4_3FIELDS = {
	typesOfWorkHomeOffice: 'typesOfWorkHomeOffice',
	typesOfWorkHomeOfficeDetails: 'typesOfWorkHomeOfficeDetails',
	occupantNameHomeOffice: 'occupantNameHomeOffice',
	occupiedAreaHomeOffice: 'occupiedAreaHomeOffice',
	// occupiedAreaUnitHomeOffice: 'occupiedAreaUnitHomeOffice',
	occupationActivityDescriptionHomeOffice:
		'occupationActivityDescriptionHomeOffice',
	isSignInstallationPlanned: 'isSignInstallationPlanned',
	startDateHomeOffice: 'startDateHomeOffice',
	endDateHomeOffice: 'endDateHomeOffice'
} as const

export type Step4_3Fields = keyof typeof STEP4_3FIELDS

/**
 * useStep4_3State - Custom hook to manage the state for step 4_3 of the GR7 commercial Permit form..
 * It retrieves the necessary answers from the Redux store and prepares the state for the form inputs.
 *
 * @returns {Object} An object containing the state for each input in step 4_3.
 */

// --- 4.3. State HOOK for Step 4.3 ---
// This hook is used to manage the state of Step 4.3 in the GR7
export function useStep4_3State() {
	const answers = useAppSelector((state) => state.request.configs)
	const { pageAssets } = requestForm()

	const isGR7_3 = isGR7_3HomeOfficeOrBusiness(answers?.whatToDo)

	return {
		// Quelle catégorie d'activités souhaitez-vous effectuer?
		typesOfWorkHomeOffice: {
			name: 'typesOfWorkHomeOffice',
			label: pageAssets?.request_form_typesOfWorkHomeOffice,
			labelKey: 'request_form_typesOfWorkHomeOffice',
			value: answers?.typesOfWorkHomeOffice?.toString() || '',
			required: isGR7_3,
			ref: useRef<HTMLSelectElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// Veuillez préciser
		typesOfWorkHomeOfficeDetails: {
			name: 'typesOfWorkHomeOfficeDetails',
			label: pageAssets?.request_form_buildingConstructionOrAdditionPermit_workDueToDisasterPrecision,
			labelKey: 'request_form_typesOfWorkHomeOfficeDetails',
			value: answers?.typesOfWorkHomeOfficeDetails || '',
			required:
				isGR7_3 &&
				answers?.typesOfWorkHomeOffice?.toString() ==
					REQUEST_CONFIGS_KEYS.homeOfficeOrBusinessType.others,
			ref: useRef<HTMLTextAreaElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// Nom de l'occupant (nom de la personne ou de l'entreprise)
		occupantNameHomeOffice: {
			name: 'occupantNameHomeOffice',
			label: pageAssets?.request_form_occupantName,
			labelKey: 'request_form_occupantName',
			value: answers?.occupantNameHomeOffice?.toString() || '',
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// Superficie occupée
		occupiedAreaHomeOffice: {
			name: 'occupiedAreaHomeOffice',
			label: pageAssets?.request_form_occupiedArea,
			labelKey: 'request_form_occupiedArea',
			value: answers?.occupiedAreaHomeOffice?.toString() || '',
			required: isGR7_3,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},
		occupiedAreaUnitHomeOffice: {
			name: 'occupiedAreaUnitHomeOffice',
			label: pageAssets?.request_form_occupiedAreaUnit,
			labelKey: 'request_form_occupiedAreaUnit',
			value:
				answers?.occupiedAreaUnitHomeOffice?.toString() ||
				REQUEST_CONFIGS_KEYS.measurement_unit.meters_unit,
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// Description des activités
		occupationActivityDescriptionHomeOffice: {
			name: 'occupationActivityDescriptionHomeOffice',
			label: pageAssets?.request_form_occupationActivityDescription,
			labelKey: 'request_form_occupationActivityDescription',
			value: answers?.occupationActivityDescriptionHomeOffice?.toString() || '',
			required: isGR7_3,
			ref: useRef<HTMLTextAreaElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// Prévoyez-vous faire l'installation d'une ou des enseigne(s)?
		isSignInstallationPlanned: {
			name: 'isSignInstallationPlanned',
			label: pageAssets?.request_form_isSignInstallationPlanned,
			labelKey: 'request_form_isSignInstallationPlanned',
			value: answers?.isSignInstallationPlanned?.toString() || '',
			required: isGR7_3,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// ****************************************
		// *Dates d'occupation
		// *******************************************
		// Date de début
		startDateHomeOffice: {
			name: 'startDateHomeOffice',
			label: pageAssets?.request_form_startDateOccupation,
			labelKey: 'request_form_startDateHomeOffice',
			value: answers?.startDateHomeOffice?.toString() || '',
			required: isGR7_3,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		},

		// Date de fin
		endDateHomeOffice: {
			name: 'endDateHomeOffice',
			label: pageAssets?.request_form_endDateOccupation,
			labelKey: 'request_form_endDateHomeOffice',
			value: answers?.endDateHomeOffice?.toString() || '',
			required: false,
			ref: useRef<HTMLInputElement>(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_4
		}
	}
}
